import React from 'react';
import { Mail, Award } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { teamMembers } from '../mock';
import SEO from '../components/SEO';

const TeamPage = () => {
  return (
    <div className="min-h-screen bg-cream-50">
      <SEO 
        title="Our Owners - Meet Emily & Megan | Manchester Dog Groomers"
        description="Meet Emily Clewlow and Megan Montgomery, the passionate owners behind Pups & Dreams Spa & Academy in Manchester. Award-winning dog groomers and trainers in Denton, Greater Manchester."
        keywords="Pups and Dreams owners, Emily Clewlow, Megan Montgomery, dog groomers Manchester, dog groomers Denton, dog grooming business Manchester, Manchester dog grooming salon owners"
      />
      {/* Hero Section */}
      <section className="bg-white !pt-[80px]">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 text-sage-900">
              Meet Our Owners
            </h1>
            <p className="text-xl leading-relaxed text-sage-700">
              The passionate, qualified professionals behind Pups & Dreams. 
              We're not just groomers – we're dog lovers dedicated to making every pup look and feel their best.
            </p>
          </div>
        </div>
      </section>

      {/* Team Members */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 max-w-5xl mx-auto">
            {teamMembers.map((member) =>
            <Card key={member.id} className="border-sage-200 hover:shadow-2xl transition-all bg-white overflow-hidden">
                <div className="relative overflow-hidden">
                  <img
                  src={member.image}
                  alt={member.name}
                  className="w-full aspect-[4/5] object-cover object-top" />

                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-6">
                    <h3 className="text-3xl font-bold text-white mb-1">{member.name}</h3>
                    <p className="text-sage-100">{member.role}</p>
                  </div>
                </div>
                <CardContent className="p-8">
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 text-sage-600">
                      <Award size={20} />
                      <span className="font-semibold">{member.qualifications}</span>
                    </div>

                    <div className="space-y-2 text-sage-700">
                      <p className="flex items-start gap-2">
                        <span className="font-semibold min-w-[100px]">Background:</span>
                        <span>{member.background}</span>
                      </p>
                      <p className="flex items-start gap-2">
                        <span className="font-semibold min-w-[100px]">Dog Mum to:</span>
                        <span>{member.dogs}</span>
                      </p>
                    </div>

                    <div className="pt-4 border-t border-sage-100">
                      <h4 className="font-bold text-sage-900 mb-2">{member.name}'s Story</h4>
                      <p className="text-sage-700 leading-relaxed text-sm">
                        {member.story}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="bg-gradient-to-br from-sage-50 to-sage-100 rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-sage-900 mb-4">Our Story</h2>
              <p className="text-sage-700 leading-relaxed mb-4">
                In 2024, Emily came to rent a grooming table at Megan's doggy daycare; a decision that turned out to be the start of something special. They soon realised they shared the same dream; to create a beautiful, dog spa and academy. Their paths had crossed for a reason... and a year later, Pups &amp; Dreams Spa &amp; Academy was born.
              </p>
              <p className="text-sage-700 leading-relaxed mb-4">
                For both Megan &amp; Emily, dog grooming has given them purpose during hard times. Our message is simple: follow your dreams; even if it means starting over. You never know where they'll lead.
              </p>
              <p className="text-sage-900 font-semibold">
                We can't wait to help you start your journey...
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-sage-900 mb-4">
                Our Approach to Grooming
              </h2>
              <p className="text-lg text-sage-600">
                What makes Pups & Dreams different
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {[
              {
                title: 'One-on-One Care',
                description: 'Your dog has our undivided attention throughout their entire appointment. No rush, no stress.'
              },
              {
                title: 'Gentle Handling',
                description: 'We use positive reinforcement and patience, especially with nervous or elderly dogs.'
              },
              {
                title: 'Transparent Process',
                description: 'We communicate every step with you and respect your dog\'s comfort levels.'
              },
              {
                title: 'Continuing Education',
                description: 'Our team regularly updates their skills with the latest grooming techniques and best practices.'
              },
              {
                title: 'Health Focused',
                description: 'We check for any health concerns during grooming and inform you of anything we notice.'
              },
              {
                title: 'Tailored Service',
                description: 'Every dog is unique. We adapt our approach to suit their individual personality and needs.'
              }].
              map((item, index) =>
              <Card key={index} className="border-sage-200 bg-gradient-to-br from-sage-50 to-white hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-sage-900 mb-2">{item.title}</h3>
                    <p className="text-sage-600">{item.description}</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </section>

    </div>);

};

export default TeamPage;